import React, { useState, useEffect, useRef } from 'react';
import {
  FaRobot, FaTimes, FaPaperPlane, FaUser, FaBrain,
  FaCog, FaStar, FaHeart, FaMicrophone,
} from 'react-icons/fa';

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [userMood] = useState('neutral');
  const messagesEndRef = useRef(null);

  // Ultra-Premium AI Response System
  const aiResponses = {
    greetings: {
      keywords: ['hello', 'hi', 'hey', 'good morning', 'good afternoon', 'good evening'],
      responses: [
        "Hello! I'm MSA Technologies AI Assistant. How can I help you today? 🚀",
        "Hi there! Welcome to MSA Technologies. What would you like to know? ✨",
        "Greetings! I'm here to assist you with all things technology. What's on your mind? 💡"
      ]
    },
    services: {
      keywords: ['services', 'what do you do', 'offerings', 'solutions', 'help'],
      responses: [
        "We offer cutting-edge software solutions including:\n• Web & Mobile Development\n• AI & Machine Learning\n• Data Science & Analytics\n• IT Consulting\n\nWhich area interests you most? 🎯",
        "MSA Technologies specializes in:\n• Custom Software Development\n• AI-Powered Solutions\n• Digital Transformation\n• Cloud Infrastructure\n\nLet me know what you're looking for! 🚀"
      ]
    },
    pricing: {
      keywords: ['price', 'cost', 'how much', 'budget', 'pricing', 'quote'],
      responses: [
        "Our pricing varies based on project scope and requirements. We offer:\n• Free consultation\n• Transparent pricing\n• Flexible payment plans\n\nWould you like a free project estimate? 💰",
        "We provide competitive pricing with:\n• No hidden costs\n• Value-based pricing\n• ROI-focused solutions\n\nLet's discuss your project needs! 📊"
      ]
    },
    portfolio: {
      keywords: ['portfolio', 'work', 'projects', 'examples', 'showcase', 'case studies'],
      responses: [
        "Check out our impressive portfolio:\n• 500+ successful projects\n• 50+ satisfied clients\n• 99% client satisfaction\n\nVisit our portfolio page to see our work! 🎨",
        "Our portfolio showcases:\n• Industry-leading solutions\n• Innovative technologies\n• Measurable results\n\nWould you like to see specific examples? 📱"
      ]
    },
    contact: {
      keywords: ['contact', 'get in touch', 'reach out', 'email', 'phone', 'support'],
      responses: [
        "Get in touch with us:\n• 📧 info@msatechnologies.com\n• 📱 +92 3419843763\n• 🌐 www.msatechnologies.com\n• 💬 Live chat available 24/7\n\nHow would you prefer to connect? 🤝",
        "We're here to help:\n• 📞 Free consultation call\n• 📨 Quick response emails\n• 💼 In-person meetings\n• 🚀 Project kickoff support\n\nWhat works best for you? 📅"
      ]
    },
    technology: {
      keywords: ['technology', 'tech stack', 'frameworks', 'languages', 'tools', 'stack'],
      responses: [
        "Our cutting-edge tech stack includes:\n• Frontend: React, Vue, Angular\n• Backend: Node.js, Django, Python\n• Mobile: React Native, Flutter\n• AI/ML: TensorFlow, PyTorch\n• Cloud: AWS, Azure, Google Cloud\n\nInterested in any specific technology? ⚡",
        "We use modern technologies:\n• Database: PostgreSQL, MongoDB\n• DevOps: Docker, Kubernetes\n• Analytics: Power BI, Tableau\n• Security: OAuth, JWT, SSL\n• Testing: Jest, Cypress\n\nWhat tech excites you? 🔬"
      ]
    },
    ai: {
      keywords: ['ai', 'artificial intelligence', 'machine learning', 'ml', 'automation', 'smart'],
      responses: [
        "Our AI solutions include:\n• 🤖 Machine Learning Models\n• 🧠 Natural Language Processing\n• 👁️ Computer Vision\n• 📊 Predictive Analytics\n• 🔮 Recommendation Systems\n\nReady to make your business AI-powered? 🚀",
        "AI is our specialty:\n• 📈 Business Process Automation\n• 🎯 Intelligent Decision Making\n• 🔍 Advanced Data Analysis\n• 💡 Smart Chatbots\n• 🌟 Personalized Experiences\n\nLet's discuss AI transformation! ✨"
      ]
    },
    sectors: {
      keywords: ['sectors', 'industries', 'finance', 'healthcare', 'education', 'retail'],
      responses: [
        "We serve diverse sectors:\n• 🏦 Finance & Banking\n• 🏥 Healthcare & Medical\n• 🎓 Education & E-learning\n• 🛒 Retail & E-commerce\n• 🏗️ Real Estate\n• 🌾 Agriculture\n\nWhich sector are you in? 🎯",
        "Industry expertise in:\n• 🔒 Security & Surveillance\n• 🎨 Arts & Media\n• 🚚 Logistics & Transportation\n• 🏭 Manufacturing\n• 💼 Professional Services\n\nTell me about your industry! 🚀"
      ]
    }
  };

  // Advanced NLP-like Response Generation
  const generateResponse = (userMessage) => {
    const message = userMessage.toLowerCase();
    let bestMatch = null;
    let highestScore = 0;

    // Calculate relevance scores for each category
    Object.entries(aiResponses).forEach(([category, data]) => {
      let score = 0;
      data.keywords.forEach(keyword => {
        if (message.includes(keyword)) {
          score += keyword.length; // Longer keywords get higher scores
        }
      });
      
      if (score > highestScore) {
        highestScore = score;
        bestMatch = category;
      }
    });

    // Generate contextual response
    if (bestMatch && highestScore > 0) {
      const responses = aiResponses[bestMatch].responses;
      const randomResponse = responses[Math.floor(Math.random() * responses.length)];
      
      // Add personalized touches based on user mood
      let personalizedResponse = randomResponse;
      if (userMood === 'excited') {
        personalizedResponse += '\n\n🎉 I can feel your enthusiasm! Let\'s make something amazing together!';
      } else if (userMood === 'concerned') {
        personalizedResponse += '\n\n🤝 Don\'t worry, we\'re here to help every step of the way.';
      }
      
      return personalizedResponse;
    }

    // Fallback responses with suggestions
    const fallbackResponses = [
      "I'm not sure I understood that. Could you rephrase or ask about:\n• Our services\n• Portfolio examples\n• Pricing information\n• Contact details\n• Technology stack",
      "Let me help you better. Try asking about:\n• What we do\n• Our expertise\n• Project examples\n• How to get started\n• Technology solutions",
      "I'd love to help! Consider asking about:\n• Our AI solutions\n• Development services\n• Industry experience\n• Client success stories\n• Getting a quote"
    ];

    return fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)];
  };

  // Smart Suggestions Based on Context
  const generateSuggestions = (lastMessage) => {
    const suggestions = [
      "Tell me about your services",
      "Show me your portfolio",
      "What's your pricing?",
      "How do I get started?",
      "Contact information",
      "AI solutions",
      "Technology stack"
    ];

    // Filter out suggestions that might be redundant
    const filteredSuggestions = suggestions.filter(suggestion => 
      !lastMessage?.toLowerCase().includes(suggestion.toLowerCase().replace('?', '').replace('?', ''))
    );

    return filteredSuggestions.slice(0, 4);
  };

  // Handle Message Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    
    // Add user message
    const newUserMessage = {
      id: Date.now(),
      text: userMessage,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString()
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    
    // Simulate AI thinking
    setIsTyping(true);
    
    // Generate AI response
    setTimeout(() => {
      const aiResponse = generateResponse(userMessage);
      const newAiMessage = {
        id: Date.now() + 1,
        text: aiResponse,
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString()
      };
      
      setMessages(prev => [...prev, newAiMessage]);
      setIsTyping(false);
      
      // Generate new suggestions
      setSuggestions(generateSuggestions(userMessage));
    }, 1500 + Math.random() * 1000); // Random delay for realism
  };

  // Handle Suggestion Click
  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    setSuggestions([]);
  };

  // Footer feature actions
  const handleRateChat = () => {
    const ratingMessage = {
      id: Date.now(),
      text:
        "Thanks for rating us! ⭐ If you have a moment, share quick feedback to help us improve. You can email us at info@msatechnologies.com or click the heart icon below.",
      sender: 'ai',
      timestamp: new Date().toLocaleTimeString(),
    };
    setMessages((prev) => [...prev, ratingMessage]);
  };

  const handleShareFeedback = () => {
    const subject = encodeURIComponent('MSA AI Assistant feedback');
    const body = encodeURIComponent(
      'Hi MSA team,\n\nHere is my feedback about the AI Assistant:\n\n'
    );
    window.location.href = `mailto:info@msatechnologies.com?subject=${subject}&body=${body}`;
  };

  const handleResetChat = () => {
    setMessages([]);
    setSuggestions([]);
    setInputValue('');
    setIsTyping(false);
  };

  const handleVoiceClick = () => {
    const SpeechRecognition =
      typeof window !== 'undefined'
        ? window.SpeechRecognition || window.webkitSpeechRecognition
        : null;

    if (!SpeechRecognition) {
      const fallback = {
        id: Date.now(),
        text:
          "Voice input isn't supported in this browser. Please type your question — I'm happy to help!",
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString(),
      };
      setMessages((prev) => [...prev, fallback]);
      return;
    }

    if (isListening) {
      setIsListening(false);
      return;
    }

    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;

      recognition.onstart = () => setIsListening(true);
      recognition.onerror = () => setIsListening(false);
      recognition.onend = () => setIsListening(false);
      recognition.onresult = (event) => {
        const transcript = event.results?.[0]?.[0]?.transcript || '';
        if (transcript) {
          setInputValue(transcript);
        }
      };

      recognition.start();
    } catch (err) {
      setIsListening(false);
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage = {
        id: Date.now(),
        text: "Hello! I'm your AI assistant from MSA Technologies. I'm here to help you discover our innovative software solutions, explore our portfolio, and get started on your next project. How can I assist you today? 🚀",
        sender: 'ai',
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages([welcomeMessage]);
      setSuggestions([
        "Tell me about your services",
        "Show me your portfolio", 
        "What's your pricing?",
        "How do I get started?"
      ]);
    }
  }, [isOpen, messages.length]);

  return (
    <>
      {/* Floating Chat Button */}
      <button
        className={`ai-chat-button ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Open AI Chat"
      >
        {isOpen ? <FaTimes /> : <FaRobot />}
        <span className="chat-button-text">AI Assistant</span>
        <div className="chat-button-pulse" />
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="ai-chat-window">
          {/* Chat Header */}
          <div className="chat-header">
            <div className="chat-header-info">
              <div className="ai-avatar">
                <FaBrain />
              </div>
              <div>
                <h3>MSA AI Assistant</h3>
                <span className="ai-status">
                  {isTyping ? 'Typing...' : 'Online'}
                  <span className="status-dot" />
                </span>
              </div>
            </div>
            <button
              className="close-chat"
              onClick={() => setIsOpen(false)}
              aria-label="Close chat"
            >
              <FaTimes />
            </button>
          </div>

          {/* Chat Messages */}
          <div className="chat-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message ${message.sender === 'user' ? 'user-message' : 'ai-message'}`}
              >
                <div className="message-avatar">
                  {message.sender === 'user' ? <FaUser /> : <FaRobot />}
                </div>
                <div className="message-content">
                  <div className="message-text">
                    {message.text.split('\n').map((line, index) => (
                      <p key={index}>{line}</p>
                    ))}
                  </div>
                  <span className="message-time">{message.timestamp}</span>
                </div>
              </div>
            ))}
            
            {/* Typing Indicator */}
            {isTyping && (
              <div className="message ai-message typing">
                <div className="message-avatar">
                  <FaRobot />
                </div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Smart Suggestions */}
          {suggestions.length > 0 && (
            <div className="chat-suggestions">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="suggestion-button"
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {/* Chat Input */}
          <form className="chat-input-form" onSubmit={handleSubmit}>
            <div className="chat-input-container">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Ask me anything about MSA Technologies..."
                className="chat-input"
                disabled={isTyping}
              />
              <div className="chat-input-actions">
                <button
                  type="button"
                  className={`voice-button ${isListening ? 'listening' : ''}`}
                  onClick={handleVoiceClick}
                  aria-label={isListening ? 'Stop voice input' : 'Start voice input'}
                >
                  <FaMicrophone />
                </button>
                <button
                  type="submit"
                  className="send-button"
                  disabled={!inputValue.trim() || isTyping}
                  aria-label="Send message"
                >
                  <FaPaperPlane />
                </button>
              </div>
            </div>
          </form>

          {/* Chat Footer */}
          <div className="chat-footer">
            <div className="chat-features">
              <button
                type="button"
                className="feature-button"
                title="Rate this chat"
                aria-label="Rate this chat"
                onClick={handleRateChat}
              >
                <FaStar />
              </button>
              <button
                type="button"
                className="feature-button"
                title="Share feedback via email"
                aria-label="Share feedback"
                onClick={handleShareFeedback}
              >
                <FaHeart />
              </button>
              <button
                type="button"
                className="feature-button"
                title="Reset conversation"
                aria-label="Reset conversation"
                onClick={handleResetChat}
              >
                <FaCog />
              </button>
            </div>
            <div className="chat-powered-by">
              Powered by <FaBrain /> MSA AI
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatbot;
